import BordPhoto from 'components/NudosComponents/BordPhoto/BordPhoto';
import { formatName } from './common';
import { EQuoteOrdersPaymentStatus, EQuoteStatus, IQuote } from 'types/quote.types';
import { IbackofficeUserData } from 'types/configuration.types';
import { QuoteResponsibles } from 'views/Quote/components/QuoteResponsibles/QuoteResponsibles';
import { QuoteProductStatusCountSummary } from '../components/QuoteProductStatusCountSummary/QuoteProductStatusCountSummary';
import { QuoteToQuoteCountSummary } from '../components/QuoteToQuoteCountSummary/QuoteToQuoteCountSummary';
import { BordBadge, BordTooltip } from 'bord-design-system';
import { QuoteOrdersList } from '../components/QuoteOrdersList/QuoteOrdersList';
import { formatShortDateAndHour } from 'utils/formatDatesAndHours';
import { getSLAStatus } from './getSLAStatus';
import { QuoteSlaTimerCell } from '../components/QuoteSlaTimerCell/QuoteSlaTimerCell';
import { getUtcOffsetHours } from 'utils/getUtcOffsetHours';

interface IItem {
  title?: string;
  label?: React.ReactNode;
  customContentElement?: React.ReactNode;
}
interface IBuilderParams {
  quote: IQuote;
  sla?: ReturnType<typeof getSLAStatus>;
  users: IbackofficeUserData[];
  refreshData?: () => void;
}

/**
 * Calcula la cantidad de herramientas sin respuesta del cliente como el total
 * de herramientas menos las aprobadas y rechazadas. Se usa tanto para las
 * "pendientes" mientras la cotización sigue activa como para las "vencidas"
 * cuando ya expiró el SLA.
 * Why: el backend no siempre reporta `totalProductsExpired`; las herramientas que
 * el cliente no aprobó ni rechazó se derivan de este cálculo.
 */
const calcUnansweredProductsCount = (quote: IQuote) => {
  const total = quote?.totalProducts ?? 0;
  const approved = quote?.totalProductsApproved ?? 0;
  const rejected = quote?.totalProductsRejected ?? 0;
  return Math.max(0, total - approved - rejected);
};

const calcReasonToBeRejected = (quote: IQuote) => {
  const expiredCount = calcUnansweredProductsCount(quote);
  if (expiredCount === quote?.totalProducts) {
    return 'Sin respuesta';
  }
  if (quote?.totalProductsRejected) {
    return 'Rechazada';
  }
  if (quote?.totalProductsApproved && !expiredCount && !quote?.totalProductsRejected) {
    return 'No concretada';
  }
  return '';
};

const getCommonCells = (quote: IQuote) => {
  const requestedByName = formatName(quote?.requestedByUser);
  return [
    {
      title: 'Cliente',
      label: (
        <div className="clientCellLayout">
          <BordPhoto
            variant="circle"
            url={quote?.organization?.imgUrl}
            oneToneIconProps={{ variant: 'building3', standardSize: 18 }}
            className="clientAvatar"
          />
          <BordTooltip message={quote?.organization?.name} position="top" customClassName="cellTextTooltip">
            <span className="cellTextLabel">{quote?.organization?.name}</span>
          </BordTooltip>
        </div>
      )
    },
    {
      title: 'Solicitado por',
      label: (
        <BordTooltip message={requestedByName} position="top" customClassName="cellTextTooltip">
          <span className="cellTextLabel">{requestedByName}</span>
        </BordTooltip>
      ),
      customClassName: 'requestedByCell'
    }
  ];
};

const getResponsiblesCell = (quote: IQuote, users: IbackofficeUserData[], refreshData?: () => void) => ({
  customContentElement: (
    <div className="responsiblesCellLayout" style={{ height: 50 }}>
      <QuoteResponsibles
        responsibleUsers={quote?.responsibleUsers}
        quoteId={quote?.id}
        users={users}
        refreshData={refreshData}
      />
    </div>
  )
});

const buildToQuoteItems = ({ quote, sla: slaData, users, refreshData }: IBuilderParams) => {
  const { className, isCTO, sla } = slaData ?? {};
  return [
    ...getCommonCells(quote),
    {
      title: 'Fecha de creación',
      label: formatShortDateAndHour(quote?.createdAt, getUtcOffsetHours())?.onlyDateAndHour
    },
    {
      title: 'Herramientas cotizadas',
      customContentElement: (
        <QuoteToQuoteCountSummary
          pendingToQuote={quote?.totalProductsPendingToQuote}
          quoted={quote?.totalProductsQuoted}
        />
      )
    },
    {
      title: 'Tiempo transcurrido',
      label: (
        <div className="elapsedTimeCell">
          <div className="elapsedTimeRow">
            <div className={`dot ${className}`}></div>
            {sla && <QuoteSlaTimerCell sla={sla} />}
          </div>
          {isCTO && <BordBadge className="fontSizeSmallBadge" size="small" variant="outlined" label="Con CTO" />}
        </div>
      )
    },
    getResponsiblesCell(quote, users, refreshData)
  ];
};

const buildPendingApprovalItems = ({ quote, sla: slaData, users, refreshData }: IBuilderParams) => {
  const { className, sla } = slaData ?? {};
  return [
    ...getCommonCells(quote),
    {
      title: 'Fecha de creación',
      label: formatShortDateAndHour(quote?.createdAt, getUtcOffsetHours())?.onlyDateAndHour
    },
    {
      title: 'Herramientas aprobadas',
      customContentElement: (
        <QuoteProductStatusCountSummary
          expired={calcUnansweredProductsCount(quote)}
          expiredTooltipMessage="Herramientas pendientes"
          rejected={quote?.totalProductsRejected}
          approved={quote?.totalProductsApproved}
        />
      )
    },
    {
      title: 'Fecha de expiración',
      label: (
        <div className="expireTimeCell">
          <div className={`dot ${className ?? ''}`}></div>
          {formatShortDateAndHour(sla?.expiresAt)?.onlyDateAndHour}
        </div>
      )
    },
    getResponsiblesCell(quote, users, refreshData)
  ];
};

const buildPurchasedItems = ({ quote }: IBuilderParams) => {
  const isAllPaid = quote?.ordersPayment?.paymentStatus === EQuoteOrdersPaymentStatus.PAID;
  return [
    ...getCommonCells(quote),
    {
      title: 'Fecha de creación',
      label: formatShortDateAndHour(quote?.createdAt, getUtcOffsetHours())?.onlyDateAndHour
    },
    {
      title: 'Herramientas compradas',
      label: (
        <div className="itemsBoughtCell">
          {`${quote?.totalProductsPurchased}/${
            quote?.totalProductsPendingApproval + quote?.totalProductsPurchased + quote?.totalProductsApproved
          }`}
          <BordBadge
            size="small"
            variant="outlined"
            {...(!isAllPaid && { color: 'warning', className: 'resetColorBadgeQuote' })}
            label={isAllPaid ? 'Pagado' : 'No pagado'}
          />
        </div>
      )
    },
    {
      customContentElement: <QuoteOrdersList ids={quote?.orderIds} orderDate={quote?.statusChangedAt} />
    }
  ];
};

const buildExpiredItems = ({ quote, sla: slaData }: IBuilderParams) => [
  ...getCommonCells(quote),
  { title: 'Fecha de creación', label: formatShortDateAndHour(quote?.createdAt, getUtcOffsetHours())?.onlyDateAndHour },
  {
    title: 'Herramientas',
    customContentElement: (
      <QuoteProductStatusCountSummary
        expired={calcUnansweredProductsCount(quote)}
        rejected={quote?.totalProductsRejected}
        approved={quote?.totalProductsApproved}
      />
    )
  },
  {
    title: 'Motivo',
    label: (
      <div className="rejectedReasonCellLayout">
        {calcReasonToBeRejected(quote) && (
          <>
            <div className="dot danger"></div>
            {calcReasonToBeRejected(quote)}
          </>
        )}
      </div>
    )
  },
  {
    title: 'Fecha de vencimiento',
    label: formatShortDateAndHour(slaData?.sla?.expiresAt, getUtcOffsetHours())?.onlyDateAndHour
  }
];

const buildRejectedItems = ({ quote }: IBuilderParams) => [
  ...getCommonCells(quote),
  { title: 'Fecha de creación', label: formatShortDateAndHour(quote?.createdAt, getUtcOffsetHours())?.onlyDateAndHour },
  { title: 'N° de productos', label: String(quote.totalProducts ?? '') },
  {
    title: 'Motivo',
    label: (
      <div className="rejectedReasonCellLayout">
        <div className="dot danger"></div>
        <BordTooltip message={quote?.customerNote} position="top" customClassName="cellTextTooltip">
          <span className="cellTextLabel">{quote?.customerNote}</span>
        </BordTooltip>
      </div>
    )
  },
  { title: 'Cancelada el', label: formatShortDateAndHour(quote?.statusChangedAt, getUtcOffsetHours())?.onlyDateAndHour }
];

const statusMap: Record<EQuoteStatus, (params: any) => IItem[]> = {
  [EQuoteStatus.TO_QUOTE]: buildToQuoteItems,
  [EQuoteStatus.PENDING_APPROVAL]: buildPendingApprovalItems,
  [EQuoteStatus.PURCHASED]: buildPurchasedItems,
  [EQuoteStatus.EXPIRED]: buildExpiredItems,
  [EQuoteStatus.REJECTED]: buildRejectedItems
};

export const getBordInfoCardItems = (
  quote: IQuote,
  status: EQuoteStatus,
  users: IbackofficeUserData[],
  refreshData?: () => void
): IItem[] => {
  const sla = getSLAStatus(quote);
  const builder = statusMap[status];

  if (!builder) return [];

  return builder({ quote, sla, users, refreshData });
};
